import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  getNextSessionSummaryLabel,
  getNextUpcomingSession,
  getSessionEventProgressLabel,
  getSessionNextEventDateLabel,
  getSessionRecurrenceSummaryLabel,
  getSessionTimeRangeLabel,
} from '@black/shared';
import { UiCardSessionViewModel, UiCardViewModel } from './ui-card.models';

export type CardSession = UiCardSessionViewModel;
@Component({
  selector: 'black-ui-card',
  templateUrl: './ui-card.component.html',
  styleUrls: ['./ui-card.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class UiCardComponent {
  @ViewChild('courseImage') courseImage!: ElementRef;

  isImageError = false;
  isSessionDetailsVisible = false;

  @Input()
  rating = 4;

  @Input()
  id = '';

  @Input()
  submitButtonText = 'Enroll Now';

  @Input()
  tutor = '';

  @Input()
  showButtons = true;

  @Input()
  fees = '';

  @Input()
  courseTitle = '';

  @Input()
  subject = '';

  @Input()
  imageBannerSource = 'https://source.unsplash.com/f1YfrZ1o2r8';

  @Input()
  description = '';

  @Input()
  isEnrolled = false;

  // New properties for additional information
  @Input()
  totalMeetings = 0;

  @Input()
  price = 0;

  @Input()
  medium = '';

  @Input()
  startDate: Date | null = null;

  @Input()
  duration = '';

  @Input()
  level = '';

  @Input()
  sessions: CardSession[] = [];

  /** Maximum number of session rows to display before showing a "+N more" indicator. */
  @Input()
  maxSessionsDisplayed = 3;

  @Input()
  set data(value: UiCardViewModel | null) {
    if (!value) {
      return;
    }

    this.id = value.id ?? this.id;
    this.courseTitle = value.courseTitle ?? this.courseTitle;
    this.subject = value.subject ?? this.subject;
    this.imageBannerSource = value.imageBannerSource ?? this.imageBannerSource;
    this.description = value.description ?? this.description;
    this.isEnrolled = value.isEnrolled ?? this.isEnrolled;
    this.totalMeetings = value.totalMeetings ?? this.totalMeetings;
    this.price = value.price ?? this.price;
    this.medium = value.medium ?? this.medium;
    this.startDate = value.startDate
      ? new Date(value.startDate)
      : this.startDate;
    this.duration = value.duration ?? this.duration;
    this.level = value.level ?? this.level;
    this.showButtons = value.showButtons ?? this.showButtons;
    this.submitButtonText = value.submitButtonText ?? this.submitButtonText;
    this.maxSessionsDisplayed =
      value.maxSessionsDisplayed ?? this.maxSessionsDisplayed;
    this.sessions = value.sessions ? [...value.sessions] : this.sessions;
  }

  @Output()
  clickMoreDetails = new EventEmitter<string>();

  @Output()
  clickEnroll = new EventEmitter<string>();

  @Output()
  clickContinueLearning = new EventEmitter<string>();

  @Output()
  clickJoinMeetings = new EventEmitter<string>();

  @Output()
  clickAccessResources = new EventEmitter<string>();

  onImageError() {
    this.isImageError = true;
  }

  getInitials(title: string): string {
    return title
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  formatPrice(price: number): string {
    if (price === 0) return 'Free';
    return `LKR ${price.toLocaleString()}`;
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get displayedSessions(): CardSession[] {
    return this.sessions.slice(0, this.maxSessionsDisplayed);
  }

  get extraSessionsCount(): number {
    return Math.max(0, this.sessions.length - this.maxSessionsDisplayed);
  }

  /** Returns the total session count. Falls back to `totalMeetings` when the `sessions`
   *  array is empty, for backward compatibility when only a count input is provided. */
  get sessionCount(): number {
    return this.sessions.length > 0 ? this.sessions.length : this.totalMeetings;
  }

  get hasMultipleSessions(): boolean {
    return this.sessions.length > 1;
  }

  get sessionSummaryLabel(): string {
    if (this.hasMultipleSessions) {
      return 'Multi-session';
    }

    const session = this.sessions[0];
    if (!session) {
      return 'No session info';
    }

    const recurrence = this.getRecurrenceSummaryPatternLabel(session);
    return recurrence || 'Single session';
  }

  getRecurrenceSummaryPatternLabel(session: CardSession): string {
    return getSessionRecurrenceSummaryLabel(session);
  }

  getNextEventDate(session: CardSession): string {
    return getSessionNextEventDateLabel(session, {
      fallbackDate: this.startDate,
    });
  }

  getSessionTime(session: CardSession): string {
    return getSessionTimeRangeLabel(session, {
      fallbackDate: this.startDate,
      now: new Date(),
    });
  }

  getSessionEventPosition(session: CardSession): string {
    return getSessionEventProgressLabel(session, {
      fallbackDate: this.startDate,
      now: new Date(),
    });
  }

  getNextSessionSummary(): string {
    return getNextSessionSummaryLabel(this.sessions, {
      fallbackDate: this.startDate,
    });
  }

  toggleSessionDetails(event?: Event) {
    event?.stopPropagation();
    this.isSessionDetailsVisible = !this.isSessionDetailsVisible;
  }

  closeSessionDetails(event?: Event) {
    event?.stopPropagation();
    this.isSessionDetailsVisible = false;
  }

  getNextSession(): CardSession | null {
    return (
      getNextUpcomingSession(this.sessions, {
        fallbackDate: this.startDate,
      })?.session ?? null
    );
  }

  onMoreDetailsClick(id: string) {
    this.clickMoreDetails.emit(id);
  }

  onClickEnroll(id: string) {
    this.clickEnroll.emit(id);
  }

  onClickContinueLearning(id: string) {
    this.clickContinueLearning.emit(id);
  }

  onClickJoinMeetings(id: string) {
    this.clickJoinMeetings.emit(id);
  }

  onClickAccessResources(id: string) {
    this.clickAccessResources.emit(id);
  }
}
